import { Box, Typography, IconButton } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Group } from '../../../ERPJobValidationSchema';
import { ERPCrewSelector } from '../components/ERPCrewSelector';
import { CrewField, SelectedEquipment } from '../RaptorJobCreateModal';
import { ERPEquipmentSelector } from '../components/ERPEquipmentSelector';
import { useERPJobResourcesStep } from '../hook/useERPJobResourcesStep';
import { ExtraSystemsSection } from '../components/ExtraSystemsSection';
import tid from '@/common/infrastructure/config/tid';
import { FEATURE_FLAGS } from '@/common/domain/constants/featureFlags';
import { useFeatureFlag } from '@/common/presentation/hooks/useFeatureFlag';
import { useJobRequirements } from '@/raptorjob/application/adapters/useJobRequirements';
import { useResourceComplianceCheck } from '@/raptorjob/application/hooks/useResourceComplianceCheck';

interface ERPJobResourcesStepProps {
  crewFields: CrewField[];
  setCrewFields: (fields: CrewField[]) => void;
  /** When set, crew list excludes employees restricted from this customer (e.g. Edit Resources modal). */
  customerId?: number;
  /** When set, crew list excludes employees restricted from this job site (e.g. Edit Resources modal). */
  jobSiteId?: number;
  /** When set, compliance requirements are also scoped to this job (edit flow). */
  jobId?: number;
  selectedEquipment: SelectedEquipment | null;
  setSelectedEquipment: (equipment: SelectedEquipment | null) => void;
  selectedClass: { id?: string | number | null; name?: string | null } | null;
  setSelectedClass: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedType: { id?: string | number | null; name?: string | null } | null;
  setSelectedType: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedSize: { id?: string | number | null; name?: string | null } | null;
  setSelectedSize: (value: { id?: string | number | null; name?: string | null } | null) => void;
  selectedMaxSize: { id?: string | number | null; name?: string | null } | null;
  setSelectedMaxSize: (value: { id?: string | number | null; name?: string | null } | null) => void;
  strict: boolean;
  setStrict: (value: boolean) => void;
  equipmentSearchTerm: string;
  setEquipmentSearchTerm: (term: string) => void;
  onUnavailableResources?: (hasUnavailableResources: boolean) => void;
}

export type ERPJobResourcesFormType = {
  groups: Group[];
  startDate?: Date | null;
  endDate?: Date | null;
  recurrence: 'single' | 'multi';
};

export const ERPJobResourcesStep = <T extends ERPJobResourcesFormType = ERPJobResourcesFormType>({
  crewFields,
  setCrewFields,
  customerId,
  jobSiteId,
  jobId,
  selectedEquipment,
  setSelectedEquipment,
  selectedClass,
  setSelectedClass,
  selectedType,
  setSelectedType,
  selectedSize,
  setSelectedSize,
  selectedMaxSize,
  setSelectedMaxSize,
  strict,
  setStrict,
  equipmentSearchTerm,
  setEquipmentSearchTerm
}: ERPJobResourcesStepProps) => {
  const resourceComplianceEnabled = useFeatureFlag(FEATURE_FLAGS.RAPTOR_JOBS_CREATE_RESOURCE_COMPLIANCE_ENABLED, true);
  const { certifications } = useJobRequirements({ customerId, jobSiteId, jobId });
  const { isEquipmentCompliant, isEmployeeCompliant, getEquipmentMissingCerts, getEmployeeMissingCerts } =
    useResourceComplianceCheck(certifications);

  const { handleAddCrewField, values } = useERPJobResourcesStep<T>({
    selectedEquipment,
    setSelectedEquipment,
    selectedClass,
    setSelectedClass,
    selectedType,
    setSelectedType,
    selectedSize,
    setSelectedSize,
    selectedMaxSize,
    setSelectedMaxSize,
    strict,
    setStrict,
    crewFields,
    setCrewFields
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
          Equipment Requirement
        </Typography>
      </Box>
      <ERPEquipmentSelector
        selectedClass={selectedClass}
        setSelectedClass={setSelectedClass}
        selectedType={selectedType}
        setSelectedType={setSelectedType}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        selectedMaxSize={selectedMaxSize}
        setSelectedMaxSize={setSelectedMaxSize}
        strict={strict}
        setStrict={setStrict}
        equipmentSearchTerm={equipmentSearchTerm}
        setEquipmentSearchTerm={setEquipmentSearchTerm}
        selectedEquipment={selectedEquipment}
        setSelectedEquipment={setSelectedEquipment}
        crewFields={crewFields}
        setCrewFields={setCrewFields}
        startDate={values.startDate}
        customerId={customerId}
        jobSiteId={jobSiteId}
        {...(resourceComplianceEnabled ? { isEquipmentCompliant, getEquipmentMissingCerts } : {})}
      />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', height: '24px' }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, lineHeight: 1 }}>
            Crew
          </Typography>
          <IconButton sx={{ p: 0 }} onClick={handleAddCrewField} {...tid('jobs:create:add-crew-field-button')}>
            <AddCircleOutlineIcon color="primary" />
          </IconButton>
        </Box>

        <ERPCrewSelector
          crewFields={crewFields}
          setCrewFields={setCrewFields}
          startDate={values.startDate}
          customerId={customerId}
          jobSiteId={jobSiteId}
          {...(resourceComplianceEnabled ? { isEmployeeCompliant, getEmployeeMissingCerts } : {})}
        />
      </Box>

      <ExtraSystemsSection />

      {/* <CreateJobGroups onUnavailableResources={onUnavailableResources} /> */}
    </Box>
  );
};
